import AsyncRender from './plugin/async-render.plugin';
import CellSelect from './plugin/cell-select.plugin';
import ColumnSelect from './plugin/column-select.plugin';
import ColumnSort from './plugin/column-sort.plugin';
import DynamicColumnWidth from './plugin/dynamic-column-width.plugin';
import RowHeader from './plugin/row-header.plugin';
import StickyHeader from './plugin/sticky-header.plugin';
import TextHighlight from './plugin/text-highlight.plugin';
import VirtualScroll from './plugin/virtual-scroll.plugin';

/**
 * Renderer Plugin Decorator
 *
 * Indexes plugins and plugin hooks to be called
 * when decorated expressions are executed in the renderer
 * @contributors franklin@modeanalytics.com
 * @example
 *
 *
 *  class plugin {
 *    transformText(out)=> {
 *      return out.replace(/world$/, 'WORLD');
 *    }
 *  };
 *
 *  let renderer = () => {
 *    let decorator = pluginDecorator(el, ['transformText']);
 *    let renderer = function(){};
 *
 *    renderer.addPlugin = decorator.registerer(renderer);
 *    renderer.render = (input)=> {
 *      decorator.decorate('transformText', input.toLowerCase());
 *    }
 *    return renderer;
 *  }
 *
 *  renderer().render('Hello World');
 *  // → 'hello world'
 *  renderer().addPlugin(plugin).render('Hello World');
 *  // → 'hello WORLD'
 *
 *
 * @param containerEl {HTMLElement}
 * @param availableHooks {array}
 * @returns {any}
 */
function pluginDecorator(containerEl: HTMLElement, availableHooks) {
  /**
   * Hooks
   *
   * plugin hook registry
   * @type {{}}
   */
  const hooks = {};

  /**
   * Plugins
   *
   * plugin registry
   * @type {{}}
   */
  const plugins = {};

  /**
   * Register
   *
   * Locally index plugin and it's hooks with {pluginDecorator.plugins}
   * registry to be called later by the {pluginDecorator.decorate} method
   * @param {function} renderer
   * @param {function} Plugin
   * @param {object} opts
   * @returns {any}
   */
  const register = function register(renderer, Plugin, opts) {
    const plugin = new Plugin(renderer, opts);
    const label = plugin.label;
    const eligibleHook = (hookName) => !!~availableHooks.indexOf(hookName);
    const addHook = (name) => ((hooks[name] || (hooks[name] = {}))[label] = plugin[name]);

    if (plugins.hasOwnProperty(label)) {
      throw new Error('Plugin already defined');
    }
    plugins[label] = { plugin, opts };
    Object.getOwnPropertyNames(Plugin.prototype).filter(eligibleHook).forEach(addHook);
    return renderer;
  };

  /**
   * Registerer
   *
   * Binds register method to the renderer
   * @param {function} renderer
   * @returns {any}
   */
  const registerer = function registerer(renderer) {
    return register.bind(null, renderer);
  };

  /**
   * Decorate
   *
   * Retrieves and composes indexed plugin callbacks to
   * apply to the output if any exist for the given name label
   * @param {any} input
   * @param {string} name
   * @returns {any}
   */
  const decorate = function decorate(input, name) {
    if (hooks.hasOwnProperty(name) && Object.keys(hooks[name]).length) {
      return Object.keys(hooks[name]).reduce((input, pluginName) => {
        const hook = hooks[name][pluginName];
        const plugin = plugins[pluginName].plugin;
        const opts = plugin.opts;

        return hook.call(plugin, input, containerEl, opts);
      }, input);
    } else {
      return input;
    }
  };

  return Object.create({ plugins, hooks, decorate, registerer });
}

export {
  pluginDecorator,
  ColumnSelect,
  ColumnSort,
  CellSelect,
  VirtualScroll,
  RowHeader,
  StickyHeader,
  DynamicColumnWidth,
  AsyncRender,
  TextHighlight,
};
